export const TYPE_LABEL: Record<string, string> = {
  yard: 'Yard sale',
  garage: 'Garage sale',
  moving: 'Moving sale',
  estate: 'Estate sale',
  auction: 'Auction',
  community: 'Community sale',
  church: 'Church sale',
  rummage: 'Rummage sale',
  flea: 'Flea / pop-up',
  farmers_market: 'Farmers market',
  event: 'Community event',
  other: 'Sale event',
};

export const TYPE_GROUPS: { key: string; label: string; types: string[] }[] = [
  { key: 'yard', label: 'Yard / Garage', types: ['yard', 'garage', 'moving'] },
  { key: 'estate', label: 'Estate', types: ['estate'] },
  { key: 'auction', label: 'Auctions', types: ['auction'] },
  { key: 'community', label: 'Community sales', types: ['community'] },
  { key: 'church', label: 'Church / Rummage', types: ['church', 'rummage'] },
  { key: 'flea', label: 'Flea / Pop-up', types: ['flea', 'other'] },
  { key: 'farmers', label: 'Farmers markets', types: ['farmers_market'] },
  { key: 'events', label: 'Events', types: ['event'] },
];

/** Thumbnail placeholder per type (no photo): a soft tint + a glyph. */
export const TYPE_LOOK: Record<string, { bg: string; glyph: string }> = {
  yard: { bg: 'linear-gradient(135deg,#dbe4f5,#b7c8ec)', glyph: '🏡' },
  garage: { bg: 'linear-gradient(135deg,#dbe4f5,#b7c8ec)', glyph: '🧰' },
  moving: { bg: 'linear-gradient(135deg,#dbe4f5,#b7c8ec)', glyph: '📦' },
  estate: { bg: 'linear-gradient(135deg,#eee4dc,#d9c0ab)', glyph: '🪑' },
  auction: { bg: 'linear-gradient(135deg,#ebe3f5,#c9b7e6)', glyph: '🔨' },
  community: { bg: 'linear-gradient(135deg,#dff0ec,#b0d9cf)', glyph: '🏘️' },
  church: { bg: 'linear-gradient(135deg,#e8e6f3,#c3c0e2)', glyph: '⛪' },
  rummage: { bg: 'linear-gradient(135deg,#e8e6f3,#c3c0e2)', glyph: '🧺' },
  flea: { bg: 'linear-gradient(135deg,#f5eadb,#e6c9a2)', glyph: '🛍️' },
  farmers_market: { bg: 'linear-gradient(135deg,#e3eedb,#b9d4a6)', glyph: '🥕' },
  event: { bg: 'linear-gradient(135deg,#fbe7e6,#efbcb8)', glyph: '🎪' },
  other: { bg: 'linear-gradient(135deg,#e5e7eb,#cbd5e1)', glyph: '🏷️' },
};

export function revealLabel(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

export function typesForGroups(keys: string[]): string[] {
  return TYPE_GROUPS.filter((g) => keys.includes(g.key)).flatMap((g) => g.types);
}

function hm(d: Date, tz: string) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  return { h: Number(p.hour), m: Number(p.minute) };
}

const dayKey = (d: Date, tz: string) => new Intl.DateTimeFormat('en-US', { timeZone: tz, dateStyle: 'short' }).format(d);

/** "9", "9:30", "12" plus its AM/PM half. */
function clock(d: Date, tz: string) {
  const { h, m } = hm(d, tz);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { t: m ? `${h12}:${String(m).padStart(2, '0')}` : String(h12), ap: h < 12 ? 'AM' : 'PM' };
}

/**
 * Hours text: "9 AM–1 PM" / "3–7 PM" for a same-day sale, "Time not posted" when the
 * host left times blank (stored as 00:00 → 00:00 next day), "9 AM onward · end time not
 * posted" when a start time is known but the source never gave an end time
 * (`endTimeUnknown`), otherwise a range with weekdays.
 */
export function hoursPill(startIso: string, endIso: string, tz: string, endTimeUnknown?: boolean): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const sm = hm(s, tz);
  const em = hm(e, tz);
  if (sm.h === 0 && sm.m === 0 && em.h === 0 && em.m === 0 && dayKey(new Date(s.getTime() + 86400e3), tz) === dayKey(e, tz)) {
    return 'Time not posted';
  }
  if (endTimeUnknown) {
    const a = clock(s, tz);
    return `${a.t} ${a.ap} onward · end time not posted`;
  }
  if (dayKey(s, tz) === dayKey(e, tz)) {
    const a = clock(s, tz);
    const b = clock(e, tz);
    // "9–1 PM" reads wrong across noon, so repeat AM/PM when it changes: "9 AM–1 PM", "3–7 PM"
    return a.ap === b.ap ? `${a.t}–${b.t} ${b.ap}` : `${a.t} ${a.ap}–${b.t} ${b.ap}`;
  }
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit' });
  return `${f.format(s)} – ${f.format(e)}`;
}

export function dayLabel(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(iso));
}

export function whenLong(startIso: string, endIso: string, tz: string, endTimeUnknown?: boolean): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const date = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' });
  const time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  if (hoursPill(startIso, endIso, tz) === 'Time not posted') return `${date.format(s)} · time not posted`;
  if (endTimeUnknown) return `${date.format(s)} · ${time.format(s)} onward (end time not posted)`;
  if (dayKey(s, tz) === dayKey(e, tz)) return `${date.format(s)} · ${time.format(s)} – ${time.format(e)}`;
  return `${date.format(s)} ${time.format(s)} – ${date.format(e)} ${time.format(e)}`;
}

/** One instant, in the listing's own timezone: "Monday, September 28 · 7:00 PM". */
export function whenAt(iso: string, tz: string): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' });
  const time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  return `${date.format(d)} · ${time.format(d)}`;
}

/** Local hour (0-23) at which the sale opens, in its own timezone. */
export function openHour(startIso: string, tz: string): number {
  const { h, m } = hm(new Date(startIso), tz);
  return h + m / 60;
}

export function mapsDirectionsUrl(address: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

export function appleMapsUrl(address: string): string {
  return `https://maps.apple.com/?daddr=${encodeURIComponent(address)}`;
}
